import React, { useState, useMemo } from 'react';
import '../../styles/accessibility.css';

/*
  Data › Onboarding Guide
  Structured landing for first‑time users entering the Data department.
  Features:
   - Hero with gradient + data analytics tagline
   - Role selector (Data Analyst, Business Intelligence, Data Scientist)
   - Adaptive checklist / journey steps per role
   - Quick links & learning tiles
   - Data & Analytics Principles highlight band
*/

const ROLES = [
  {
    id: 'data-analyst',
    label: 'Data Analyst',
    color: 'from-blue-600 to-cyan-600',
    kpis: ['Data Quality Score', 'Report Accuracy', 'Query Performance'],
    steps: [
      'Explore analytics & intelligence dashboard',
      'Review raw intelligence data sources',
      'Configure custom report templates',
      'Set up data quality monitoring alerts'
    ]
  },
  {
    id: 'business-intelligence',
    label: 'Business Intelligence Lead',
    color: 'from-indigo-600 to-purple-600',
    kpis: ['Market Insights', 'Investor Engagement', 'Performance Trends'],
    steps: [
      'Analyze market analysis trends',
      'Review investor dashboard metrics',
      'Examine performance metrics overview',
      'Export first BI insights report'
    ]
  },
  {
    id: 'data-scientist',
    label: 'Data Scientist',
    color: 'from-violet-600 to-fuchsia-600',
    kpis: ['Model Accuracy', 'Predictive Power', 'Data Coverage'],
    steps: [
      'Review predictive analytics models',
      'Validate trust score analysis methods',
      'Configure privacy assessment parameters',
      'Run initial security evaluation scan'
    ]
  }
];

const QUICK_LINKS = [
  { title: 'Analytics & Intelligence', path: '/data/analytics-and-intelligence/raw-intelligence', icon: 'bi-graph-up' },
  { title: 'Market Analysis', path: '/data/business-intelligence/market-analysis', icon: 'bi-bar-chart-line' },
  { title: 'Predictive Analytics', path: '/data/analytics-and-intelligence/predictive-analytics', icon: 'bi-cpu' },
  { title: 'Investor Dashboard', path: '/data/investor-dashboard', icon: 'bi-briefcase' },
  { title: 'Custom Reports', path: '/data/analytics-and-intelligence/custom-reports', icon: 'bi-file-earmark-text' },
  { title: 'Performance Metrics', path: '/data/business-intelligence/performance-metrics', icon: 'bi-speedometer' }
];

export default function DataOnboardingGuide(){
  const [role, setRole] = useState(ROLES[0]?.id);
  const active = useMemo(()=> ROLES.find(r=> r.id===role), [role]);
  const [completed, setCompleted] = useState(new Set());
  function toggle(step){
    setCompleted(prev=>{ const n = new Set(prev); n.has(step) ? n.delete(step) : n.add(step); return n; });
  }

  return (
    <div className="min-h-full space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-indigo-800 to-violet-700 text-white p-10 shadow-lg">
        <div className="max-w-4xl relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Data Department Onboarding</h1>
          <p className="text-white/80 text-lg leading-relaxed mb-6">
            Welcome to the Data Department. Here, we transform raw intelligence into actionable insights, predictive models, and strategic business intelligence. Every data point tells a story, and every analysis drives decision-making. Let's unlock the power of data-driven transformation together.
          </p>
          <div className="flex flex-wrap gap-3">
            {ROLES.map(r=> (
              <button
                key={r.id}
                onClick={()=> setRole(r.id)}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium transition-all shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/60',
                  role===r.id ? 'bg-white text-blue-800' : 'bg-white/10 hover:bg-white/20 text-white'
                ].join(' ')}
                aria-pressed={role===r.id}
              >{r.label}</button>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{backgroundImage:'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25), transparent 60%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.18), transparent 55%)'}} />
      </section>

      {/* Role Snapshot & Checklist */}
      <section className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-1 text-slate-800">{active.label} Journey</h2>
            <p className="text-slate-600 text-sm mb-4">Complete the high‑impact activation steps below. Progress auto‑persists in this session.</p>
            <ol className="space-y-3 list-decimal list-inside">
              {active.steps.map(step=>{
                const done = completed.has(step);
                return (
                  <li key={step} className="pl-1">
                    <button
                      onClick={()=> toggle(step)}
                      className={[ 'group w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 transition-colors', done ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'].join(' ')}
                      aria-pressed={done}
                    >
                      <span className={['mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold', done ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600 group-hover:bg-slate-300'].join(' ')}>
                        {done ? '✓' : active.steps.indexOf(step)+1}
                      </span>
                      <span className={['flex-1 text-sm', done ? 'line-through text-blue-700' : 'text-slate-700'].join(' ')}>{step}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-5 flex items-center gap-3 text-sm">
              <div className="flex h-2 rounded bg-slate-200 w-40 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500" style={{width: (completed.size/active.steps.length*100)+'%'}} />
              </div>
              <span className="text-slate-600">{completed.size}/{active.steps.length} complete</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {active.kpis.map(k=> (
              <div key={k} className="relative rounded-xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden group">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-slate-50 to-blue-50" />
                <div className="relative">
                  <p className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-1">Focus KPI</p>
                  <h3 className="text-slate-800 font-semibold text-sm mb-2">{k}</h3>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-1 font-medium">Simulated</span>
                    <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 text-[11px] px-2 py-1 font-medium">Track</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <aside className="space-y-6">
          <div className="border border-slate-200 rounded-xl bg-white shadow-sm p-5">
            <h3 className="text-slate-800 font-semibold mb-3 text-sm tracking-wide uppercase">Quick Launch</h3>
            <ul className="space-y-2">
              {QUICK_LINKS.map(link=> (
                <li key={link.path}>
                  <a href={link.path} className="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors">
                    <i className={`bi ${link.icon} text-slate-400 group-hover:text-blue-500`} />
                    <span className="flex-1">{link.title}</span>
                    <span className="text-[10px] text-slate-400 group-hover:text-blue-500">›</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-blue-200 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-sm">
            <h3 className="text-blue-800 font-semibold mb-2 text-sm tracking-wide uppercase">Data & Analytics Pillars</h3>
            <ul className="text-xs text-slate-600 space-y-1">
              <li><strong className="text-slate-800">Accuracy:</strong> Data quality, validation & integrity checks.</li>
              <li><strong className="text-slate-800">Insights:</strong> Actionable intelligence from raw data analysis.</li>
              <li><strong className="text-slate-800">Predictability:</strong> Advanced modeling & forecasting capabilities.</li>
              <li><strong className="text-slate-800">Security:</strong> Privacy-first data handling & compliance standards.</li>
            </ul>
          </div>
        </aside>
      </section>

      {/* Learning Tiles */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Explore Data Domains</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <LearningTile title="Analytics & Intelligence" desc="Transform raw data into strategic insights with predictive models & custom reports." path="/data/analytics-and-intelligence/raw-intelligence" gradient="from-blue-600 to-cyan-500" />
          <LearningTile title="Business Intelligence" desc="Drive decisions with market analysis, investor dashboards & performance tracking." path="/data/business-intelligence/market-analysis" gradient="from-indigo-600 to-purple-500" />
          <LearningTile title="Trust & Security Scanning" desc="Assess trust scores, privacy compliance & security evaluation metrics." path="/bread/trust-and-security-scanning/trust-score-analysis" gradient="from-violet-600 to-fuchsia-500" />
        </div>
      </section>

      <footer className="pt-4 pb-10 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Trust Tech Studio • Data Onboarding Fabric (Simulated Data)
      </footer>
    </div>
  );
}

function LearningTile({ title, desc, path, gradient }){
  return (
    <a href={path} className="group relative block rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br ${gradient} mix-blend-multiply`} />
      <div className="relative p-6 space-y-3">
        <h3 className="text-slate-800 group-hover:text-white font-semibold tracking-tight text-base transition-colors">{title}</h3>
        <p className="text-slate-600 group-hover:text-white/90 text-sm leading-relaxed">{desc}</p>
        <span className="inline-flex items-center gap-1 text-blue-600 group-hover:text-white text-xs font-medium">Explore <span className="translate-x-0 group-hover:translate-x-0.5 transition-transform">›</span></span>
      </div>
    </a>
  );
}
